import { Injectable } from '@angular/core';
import { GenericService } from '../../generic.service';
import { TroubleShootingStep } from '../interface/troubleshoot-eult.interface';
import { defaultLanguage } from '../../shared/constants/language.constants';
import { LandingService } from '../../landing-page/service/landing.service';

@Injectable({
  providedIn: 'root',
})
export class TroubleShootEuhtService {
  stepsList: any;
  issueName: string | undefined = '';
  currentLanguageName: string = defaultLanguage;

  constructor(
    private genericService: GenericService,
    private landingService: LandingService,
  ) {
    try {
      const stored = localStorage.getItem('deviceData');
      const selected = stored ? JSON.parse(stored).languageSelected : null;
      this.currentLanguageName = selected || defaultLanguage;
    } catch {
      this.currentLanguageName = defaultLanguage;
    }

    this.landingService.languageName$.subscribe((langName) => {
      this.currentLanguageName = langName || defaultLanguage;
    });
  }

  getTroubleShootEuhtIssues(): any[] {
    let issues: any[] = [];
    this.stepsList = [];

    for (const option of this.euht.options) {
      if (
        option.key === 'euht-machine-is-down' ||
        option.key === 'euht-not-draining'
      ) {
        if (option.steps && option.steps.length > 1) {
          const machineDownSteps = option.steps.map((step) => {
            return { key: step.key, label: option.label };
          });

          issues.push({ key: machineDownSteps[0].key, label: option.label });
          this.stepsList.push(...machineDownSteps);
        } else {
          issues.push({ key: option.key, label: option.label });
        }
      } else {
        if (option.steps && option.steps.length > 0) {
          const otherIssueStepKey = option.steps[0].key;
          issues.push({ key: otherIssueStepKey, label: option.label });
          this.stepsList.push(...option.steps);
        } else {
          issues.push({ key: option.key, label: option.label });
        }
      }
    }

    return issues;
  }

  setIssuename(issuename: string | undefined) {
    this.issueName = issuename;
  }

  getIssuename() {
    return this.issueName;
  }

  getTroubleShootStep(searchKey: string): TroubleShootingStep | null {
    return this.genericService.findObjectByKey(searchKey, this.euht.options);
  }

  getStepState() {
    return this.stepsList;
  }

  getStepIndex(key: string) {
    for (let i = 0; i < this.stepsList.length; i++) {
      if (this.stepsList[i].key === key) {
        return i;
      }
    }
    return -1;
  }

  euht: TroubleShootingStep = {
    key: 'euht',
    options: [
      {
        key: 'euht-machine-is-down',
        label: [
          'Machine is down',
          'Máquina no operativa',
          'La machine est en panne',
          'Machine is buiten bedrijf',
          'Macchina fuori servizio',
          '機械が停止している',
          'Máquina fora de serviço',
          'Maschine außer Betrieb',
        ],
        option: 'troubleshooting.euht.machineDown',
        steps: [
          {
            key: 'euht-filling-with-water',
            question: 'troubleshooting.es2000.machineDownFilling',
            label: [
              'Is the machine filling with water?',
              '¿La máquina esta llenandose con agua?',
              'La machine se remplit d’eau ?',
              'Wordt de machine gevuld met water?',
              'La macchina si sta riempiendo di acqua?',
              '機械に給水されていますか？',
              'A máquina está enchendo com água?',
              'Füllt sich die Maschine mit Wasser?',
            ],
            optionsLabel: '',
            description: ['', '', '', '', '', '', '', ''],
            images: [
              {
                url: '',
                alt: '',
              },
            ],
            options: [
              {
                key: 'euht-yes',
                option: 'troubleshooting.yes',
                label: ['Yes', 'Si', 'Oui', 'Ja', 'Sì', 'はい', 'Sim', 'Ja'],
                icon: 'check',
                steps: [
                  {
                    key: 'euht-machinelist-items',
                    question: 'Checklist Items',
                    options: [],
                    machineIssueListItems: [
                      {
                        title: {
                          english: 'machine is powered on',
                          spanish: 'la máquina está encendida',
                          french: 'La machine est allumée',
                          dutch: 'machine staat aan',
                          italian: 'la macchina è accesa',
                          japanese: '機械の電源が入っている',
                          portuguese: 'a máquina está ligada',
                          german: 'Maschine ist eingeschaltet',
                        },
                        details: {
                          english: [
                            '1. Ensure the machine is plugged in and powered on.',
                            '2. Check the light to confirm the machine is on.',
                            '3. Run a cycle following the proper procedure.',
                            '4. Check the breaker box to confirm power.',
                          ],
                          spanish: [
                            '1. Asegúrese de que la máquina esté enchufada y encendida.',
                            '2. Verifique la luz para confirmar que la máquina esté encendida.',
                            '3. Ejecute un ciclo siguiendo el procedimiento adecuado.',
                            '4. Verifique la caja de interruptores para confirmar la energía.',
                          ],
                          french: [
                            '1. Assurez-vous que la machine est branchée et allumée.',
                            '2. Vérifiez la lumière pour confirmer que la machine est allumée.',
                            '3. Exécutez un cycle en suivant la procédure appropriée.',
                            '4. Vérifiez le tableau de disjoncteurs pour confirmer l’alimentation.',
                          ],
                          dutch: [
                            '1. Zorg dat de machine is ingeplugd en ingeschakeld.',
                            '2. Controleer het lampje om te bevestigen dat de machine aan staat.',
                            '3. Voer een cyclus uit volgens de juiste procedure.',
                            '4. Controleer in de groepenkast of er spanning is.',
                          ],
                          italian: [
                            '1. Assicurarsi che la macchina sia collegata e accesa.',
                            '2. Controllare la spia per confermare che la macchina sia accesa.',
                            '3. Avviare un ciclo seguendo la procedura corretta.',
                            '4. Verificare nel quadro elettrico che ci sia alimentazione.',
                          ],
                          japanese: [
                            '1. 機械がコンセントに接続され、電源が入っていることを確認してください。',
                            '2. 通電ランプを確認し、機械がオンであることを確認してください。',
                            '3. 正しい手順に従ってサイクルを実行してください。',
                            '4. 分電盤（ブレーカーボックス）で電源が入っているか確認してください。',
                          ],
                          portuguese: [
                            '1. Certifique-se de que a máquina está conectada e ligada.',
                            '2. Verifique a luz para confirmar que a máquina está ligada.',
                            '3. Execute um ciclo seguindo o procedimento correto.',
                            '4. Verifique o quadro/disjuntor para confirmar a alimentação.',
                          ],
                          german: [
                            '1. Stellen Sie sicher, dass die Maschine eingesteckt und eingeschaltet ist.',
                            '2. Prüfen Sie die Kontrollleuchte, um zu bestätigen, dass die Maschine an ist.',
                            '3. Führen Sie einen Zyklus gemäß der richtigen Vorgehensweise durch.',
                            '4. Prüfen Sie den Sicherungskasten, um die Stromversorgung zu bestätigen.',
                          ],
                        },
                        answered: false,
                      },
                      {
                        title: {
                          english: 'incoming water temperature is above 120°F',
                          spanish:
                            'La temperatura del agua entrante es superior a 120 °F.',
                          french:
                            'La température de l’eau entrante dépasse 120°F.',
                          dutch:
                            'de temperatuur van het binnenkomende water is boven 120°F',
                          italian:
                            'la temperatura dell’acqua in ingresso è superiore a 120°F',
                          japanese: '給水温度が120°F以上である',
                          portuguese:
                            'a temperatura da água de entrada está acima de 120°F',
                          german:
                            'die einströmende Wassertemperatur liegt über 120°F',
                        },
                        details: {
                          english: [
                            'Wait for 15 minutes and see if the temperature meets the standard. If not, please use the “I need help” button below to contact Ecolab',
                          ],
                          spanish: [
                            'Espere 15 minutos y vea si la temperatura cumple con el estándar. De lo contrario, utilice el botón "Necesito ayuda" a continuación para comunicarse con Ecolab.',
                          ],
                          french: [
                            'Attendez 15 minutes et vérifiez si la température respecte la norme. Sinon, utilisez le bouton “J’ai besoin d’aide” ci-dessous pour contacter Ecolab.',
                          ],
                          dutch: [
                            'Wacht 15 minuten en controleer of de temperatuur aan de norm voldoet. Zo niet, gebruik dan de knop "Ik heb hulp nodig" hieronder om contact op te nemen met Ecolab',
                          ],
                          italian: [
                            'Attendere 15 minuti e verificare che la temperatura rispetti lo standard. In caso contrario, utilizzare il pulsante “Ho bisogno di aiuto” qui sotto per contattare Ecolab',
                          ],
                          japanese: [
                            '15分待って温度が基準に達しているか確認してください。満たない場合は、下の「助けが必要です」ボタンを使用してエコラボに連絡してください。',
                          ],
                          portuguese: [
                            'Aguarde 15 minutos e verifique se a temperatura atende ao padrão. Caso contrário, use o botão “Preciso de ajuda” abaixo para contactar a Ecolab',
                          ],
                          german: [
                            'Warten Sie 15 Minuten und prüfen Sie, ob die Temperatur dem Standard entspricht. Wenn nicht, verwenden Sie bitte die Schaltfläche „Ich brauche Hilfe“, um Ecolab zu kontaktieren.',
                          ],
                        },
                        answered: false,
                      },
                      {
                        title: {
                          english: 'screen is not frozen',
                          spanish: 'la pantalla no está congelada',
                          french: 'L’écran n’est pas gelé',
                          dutch: 'scherm is niet vastgelopen',
                          italian: 'lo schermo non è bloccato',
                          japanese: '画面がフリーズしていない',
                          portuguese: 'a tela não está travada',
                          german: 'Bildschirm ist nicht eingefroren',
                        },
                        details: {
                          english: ['If frozen, unplug and plug back in.'],
                          spanish: [
                            'Si está congelada, desenchufe y vuelva a enchufar.',
                          ],
                          french: [
                            'En cas de gel, débranchez-le puis rebranchez-le.',
                          ],
                          dutch: [
                            'Als het scherm vastloopt, stekker eruit en weer insteken.',
                          ],
                          italian: [
                            'Se bloccato, scollegare e ricollegare la spina.',
                          ],
                          japanese: [
                            'フリーズしている場合は、電源プラグを抜いてから差し直してください。',
                          ],
                          portuguese: [
                            'Se estiver travada, retire da tomada e conecte novamente.',
                          ],
                          german: [
                            'Falls eingefroren: Stecker ziehen und wieder einstecken.',
                          ],
                        },
                        answered: false,
                      },
                    ],
                  },
                ],
              },
              {
                key: 'euht-not-sure',
                option: 'troubleshooting.notSure',
                label: [
                  'Not Sure',
                  'No estoy seguro(a)',
                  'Pas sûr',
                  'Niet zeker',
                  'Non sono sicuro(a)',
                  'よくわからない',
                  'Não tenho certeza',
                  'Nicht sicher',
                ],
                icon: 'question',
                resolve: false,
                steps: [],
              },
              {
                key: 'euht-no',
                option: 'troubleshooting.no',
                label: [
                  'No',
                  'No',
                  'Non',
                  'Nee',
                  'No',
                  'いいえ',
                  'Não',
                  'Nein',
                ],
                icon: 'cross',
                steps: [
                  {
                    key: 'euht-machinelist-items-no',
                    question: 'Checklist Items for No',
                    options: [],
                    machineIssueListItems: [
                      {
                        title: {
                          english: 'machine is powered on',
                          spanish: 'la máquina está encendida',
                          french: 'La machine est allumée',
                          dutch: 'machine staat aan',
                          italian: 'la macchina è accesa',
                          japanese: '機械の電源が入っている',
                          portuguese: 'a máquina está ligada',
                          german: 'Maschine ist eingeschaltet',
                        },
                        details: {
                          english: [
                            '1. Ensure the machine is plugged in and powered on.',
                            '2. Check the light to confirm the machine is on.',
                            '3. Run a cycle following the proper procedure.',
                            '4. Check the breaker box to confirm power.',
                          ],
                          spanish: [
                            '1. Asegúrese de que la máquina esté enchufada y encendida.',
                            '2. Verifique la luz para confirmar que la máquina esté encendida.',
                            '3. Ejecute un ciclo siguiendo el procedimiento adecuado.',
                            '4. Verifique la caja de interruptores para confirmar la energía.',
                          ],
                          french: [
                            '1. Assurez-vous que la machine est branchée et allumée.',
                            '2. Vérifiez la lumière pour confirmer que la machine est allumée.',
                            '3. Exécutez un cycle en suivant la procédure appropriée.',
                            '4. Vérifiez le tableau de disjoncteurs pour confirmer l’alimentation.',
                          ],
                          dutch: [
                            '1. Zorg dat de machine is ingeplugd en ingeschakeld.',
                            '2. Controleer het lampje om te bevestigen dat de machine aan staat.',
                            '3. Voer een cyclus uit volgens de juiste procedure.',
                            '4. Controleer in de groepenkast of er spanning is.',
                          ],
                          italian: [
                            '1. Assicurarsi che la macchina sia collegata e accesa.',
                            '2. Controllare la spia per confermare che la macchina sia accesa.',
                            '3. Avviare un ciclo seguendo la procedura corretta.',
                            '4. Verificare nel quadro elettrico che ci sia alimentazione.',
                          ],
                          japanese: [
                            '1. 機械がコンセントに接続され、電源が入っていることを確認してください。',
                            '2. 通電ランプを確認し、機械がオンであることを確認してください。',
                            '3. 正しい手順に従ってサイクルを実行してください。',
                            '4. 分電盤（ブレーカーボックス）で電源が入っているか確認してください。',
                          ],
                          portuguese: [
                            '1. Certifique-se de que a máquina está conectada e ligada.',
                            '2. Verifique a luz para confirmar que a máquina está ligada.',
                            '3. Execute um ciclo seguindo o procedimento correto.',
                            '4. Verifique o quadro/disjuntor para confirmar a alimentação.',
                          ],
                          german: [
                            '1. Stellen Sie sicher, dass die Maschine eingesteckt und eingeschaltet ist.',
                            '2. Prüfen Sie die Kontrollleuchte, um zu bestätigen, dass die Maschine an ist.',
                            '3. Führen Sie einen Zyklus gemäß der richtigen Vorgehensweise durch.',
                            '4. Prüfen Sie den Sicherungskasten, um die Stromversorgung zu bestätigen.',
                          ],
                          images: [],
                        },
                        answered: false,
                      },
                      {
                        keyValue: 1,
                        title: {
                          english: 'water is on',
                          spanish: 'el agua esta encendida',
                          french: 'L’eau est allumée',
                          dutch: 'het water staat aan',
                          italian: "l'acqua è aperta",
                          japanese: '水が出ている',
                          portuguese: 'a água está ligada',
                          german: 'Wasser ist eingeschaltet',
                        },
                        details: {
                          english: [
                            '1. Ensure the water shutdown valve is turned on',
                            '2. Turn on the valve',
                          ],
                          spanish: [
                            '1. Asegúrese de que la válvula de cierre de agua esté abierta.',
                            '2. Abra la válvula.',
                          ],
                          french: [
                            "1. Assurez-vous que le robinet d'arrêt d'eau est ouvert.",
                            '2. Ouvrez la vanne.',
                          ],
                          dutch: [
                            '1. Zorg dat de afsluitkraan voor water open staat.',
                            '2. Draai de kraan open.',
                          ],
                          italian: [
                            '1. Assicurarsi che la valvola d’intercettazione dell’acqua sia aperta.',
                            '2. Aprire la valvola.',
                          ],
                          japanese: [
                            '1. 給水の止水バルブが開いていることを確認してください。',
                            '2. バルブを開けてください。',
                          ],
                          portuguese: [
                            '1. Certifique-se de que a válvula de fechamento da água está aberta.',
                            '2. Abra a válvula.',
                          ],
                          german: [
                            '1. Stellen Sie sicher, dass das Absperrventil für das Wasser geöffnet ist.',
                            '2. Öffnen Sie das Ventil.',
                          ],
                          images: [
                            {
                              url: 'assets/images/troubleshooting/eult/eult-machine-down-1.png',
                            },
                          ],
                        },
                        answered: false,
                      },
                      {
                        keyValue: 2,
                        title: {
                          english: 'float switch is not stuck',
                          spanish:
                            'El interruptor de flotador no está atascado.',
                          french: 'L’interrupteur à flotteur n’est pas bloqué',
                          dutch: 'vlotterschakelaar zit niet vast',
                          italian:
                            'l’interruttore a galleggiante non è bloccato',
                          japanese: 'フロートスイッチが固着していない',
                          portuguese: 'a boia (float switch) não está presa',
                          german: 'Schwimmerschalter klemmt nicht',
                        },
                        details: {
                          english: ['clean it after every meal service'],
                          spanish: [
                            'Límpialo después de cada servicio de comida.',
                          ],
                          french: ['le nettoyer après chaque service de repas'],
                          dutch: ['na elk maaltijdmoment reinigen'],
                          italian: ['pulirlo dopo ogni servizio pasti'],
                          japanese: ['各食事サービス後に清掃してください'],
                          portuguese: ['limpe-o após cada serviço de refeição'],
                          german: ['nach jedem Mahlzeitenservice reinigen'],
                          images: [
                            {
                              url: 'assets/images/troubleshooting/eult/eult-machine-down-2.png',
                            },
                            {
                              url: 'assets/images/troubleshooting/eult/eult-machine-down-3.png',
                            },
                          ],
                        },
                        answered: false,
                      },
                      {
                        title: {
                          english: 'drain is shut',
                          spanish: 'el drenaje está cerrado',
                          french: 'Le drain est fermé',
                          dutch: 'afvoer is gesloten',
                          italian: 'lo scarico è chiuso',
                          japanese: 'ドレンが閉まっている',
                          portuguese: 'o dreno está fechado',
                          german: 'Abfluss ist geschlossen',
                        },
                        details: {
                          english: [],
                          spanish: [],
                          french: [],
                          dutch: [],
                          italian: [],
                          japanese: [],
                          portuguese: [],
                          german: [],
                        },
                        answered: false,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        key: 'euht-leaking',
        label: [
          'Leaking',
          'Fugas',
          'Fuite',
          'Lekt',
          'Perdita',
          '漏れている',
          'Vazamento',
          'Leckage',
        ],
        option: 'troubleshooting.euht.leaking',
        steps: [
          {
            key: 'euht-leakinglist-items',
            options: [],
            machineIssueListItems: [
              {
                keyValue: 4,
                title: {
                  english: 'wash and rinse arms in place',
                  spanish: 'lavar y enjuagar los brazos en su lugar',
                  french: 'Lavez et rincez les bras en place',
                  dutch: 'was- en spoelarmen op hun plaats',
                  italian: 'bracci di lavaggio e risciacquo in posizione',
                  japanese: '洗浄・すすぎアームが正しく取り付けられている',
                  portuguese: 'braços de lavagem e enxágue no lugar',
                  german: 'Wasch- und Spülarme sind an ihrem Platz',
                },
                details: {
                  english: [],
                  spanish: [],
                  french: [],
                  dutch: [],
                  italian: [],
                  japanese: [],
                  portuguese: [],
                  german: [],
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eult/eult-leaking-1.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/eult/eult-leaking-2.png',
                    },
                  ],
                },
                answered: false,
              },
              {
                keyValue: 5,
                title: {
                  english: 'float switch is not stuck',
                  spanish: 'El interruptor de flotador no está atascado.',
                  french: 'L’interrupteur à flotteur n’est pas bloqué',
                  dutch: 'vlotterschakelaar zit niet vast',
                  italian: 'l’interruttore a galleggiante non è bloccato',
                  japanese: 'フロートスイッチが固着していない',
                  portuguese: 'a boia (float switch) não está presa',
                  german: 'Schwimmerschalter klemmt nicht',
                },
                details: {
                  english: ['clean it after every meal service'],
                  spanish: ['Límpialo después de cada servicio de comida.'],
                  french: ['le nettoyer après chaque service de repas'],
                  dutch: ['na elk maaltijdmoment reinigen'],
                  italian: ['pulirlo dopo ogni servizio pasti'],
                  japanese: ['各食事サービス後に清掃してください'],
                  portuguese: ['limpe-o após cada serviço de refeição'],
                  german: ['nach jedem Mahlzeitenservice reinigen'],
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eult/eult-machine-down-2.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/eult/eult-machine-down-3.png',
                    },
                  ],
                },
                answered: false,
              },
              {
                title: {
                  english:
                    'drain connection not clogged / plugged (if accessible)',
                  spanish:
                    'conexión de drenaje no obstruida/taponada (si es accesible)',
                  french:
                    'connexion de drainage non obstruée/emplombée (si accessible)',
                  dutch:
                    'afvoerverbinding niet verstopt/afgedopt (indien toegankelijk)',
                  italian:
                    'collegamento di scarico non ostruito/tappato (se accessibile)',
                  japanese:
                    '排水接続部が詰まっていない／塞がれていない（アクセス可能な場合）',
                  portuguese:
                    'conexão do dreno não obstruída/vedada (se acessível)',
                  german:
                    'Ablaufanschluss nicht verstopft/verschlossen (falls zugänglich)',
                },
                details: {
                  english: [],
                  spanish: [],
                  french: [],
                  dutch: [],
                  italian: [],
                  japanese: [],
                  portuguese: [],
                  german: [],
                },
                answered: false,
              },
              {
                keyValue: 6,
                title: {
                  english: 'drain hoses/tubes not kinked (if accessible)',
                  spanish:
                    'mangueras/tubos de drenaje no retorcidos (si son accesibles)',
                  french: 'tuyaux/tubes de drain non pliés (si accessible)',
                  dutch:
                    'afvoerslangen/-buizen niet geknikt (indien toegankelijk)',
                  italian:
                    'tubi/flessibili di scarico non piegati (se accessibili)',
                  japanese:
                    '排水ホース／チューブが折れ曲がっていない（アクセス可能な場合）',
                  portuguese:
                    'mangueiras/tubos de dreno sem dobras (se acessível)',
                  german:
                    'Ablaufschläuche/-rohre nicht geknickt (falls zugänglich)',
                },
                details: {
                  english: [],
                  spanish: [],
                  french: [],
                  dutch: [],
                  italian: [],
                  japanese: [],
                  portuguese: [],
                  german: [],
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eult/eult-leaking-3.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/eult/eult-leaking-4.png',
                    },
                  ],
                },
                answered: false,
              },
              {
                title: {
                  english:
                    'the drain hose/tube has a loop to prevent siphoning (if accessible)',
                  spanish:
                    'la manguera/tubo de drenaje tiene un bucle para evitar el sifón (si es accesible)',
                  french:
                    'le tuyau de vidange a une boucle pour éviter le siphon (si accessible)',
                  dutch:
                    'de afvoerslang/-buis heeft een lus om hevelwerking te voorkomen (indien toegankelijk)',
                  italian:
                    'il tubo/flessibile di scarico ha un’ansa per evitare il sifonaggio (se accessibile)',
                  japanese:
                    '排水ホース／チューブにサイフォン防止用のループがある（アクセス可能な場合）',
                  portuguese:
                    'a mangueira/tubo de dreno tem um laço para evitar sifonagem (se acessível)',
                  german:
                    'Ablaufschlauch/-rohr hat eine Schlaufe zur Vermeidung von Siphoneffekt (falls zugänglich)',
                },
                details: {
                  english: [],
                  spanish: [],
                  french: [],
                  dutch: [],
                  italian: [],
                  japanese: [],
                  portuguese: [],
                  german: [],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'euht-cycle-issue',
        label: [
          'Cycle Issue',
          'Problema del ciclo',
          'Problème de cycle',
          'Probleem met cyclus',
          'Problema del ciclo',
          'サイクルの問題',
          'Problema de ciclo',
          'Zyklusproblem',
        ],
        option: 'troubleshooting.euht.cycle',
        steps: [
          {
            key: 'euht-cycle-issue-items',
            options: [],
            machineIssueListItems: [
              {
                keyValue: 3,
                title: {
                  english: 'cut power source and turn it back on',
                  spanish: 'Corte la fuente de energía y vuelva a encenderla',
                  french: 'Coupez la source d’alimentation et rallumez-la',
                  dutch: 'haal de stroom eraf en zet vervolgens weer aan',
                  italian: 'togliere alimentazione e riaccendere',
                  japanese: '電源を切り、再度入れてください',
                  portuguese: 'desligue a alimentação e ligue novamente',
                  german: 'Stromquelle trennen und wieder einschalten',
                },
                details: {
                  english: [
                    'Turn off the power and breaker in the service panel, wait 1 minute, then turn it back on and cycle the machine',
                  ],
                  spanish: [
                    'Apague la energía y el disyuntor en el panel de servicio, espere 1 minuto, luego vuelva a encenderlo y cycle la máquina.',
                  ],
                  french: [
                    'Coupez l’alimentation et le disjoncteur dans le panneau de service, attendez 1 minute, puis rallumez et faites tourner la machine.',
                  ],
                  dutch: [
                    'Zet de stroom en zekering/groep in het servicepaneel uit, wacht 1 minuut, zet weer aan en laat de machine een cyclus draaien',
                  ],
                  italian: [
                    "Spegnere l'alimentazione e l'interruttore nel quadro, attendere 1 minuto, quindi riaccendere e far eseguire un ciclo alla macchina",
                  ],
                  japanese: [
                    'サービスパネルの主電源とブレーカーを切り、1分待ってから再投入し、サイクルを実行してください。',
                  ],
                  portuguese: [
                    'Desligue a energia e o disjuntor no painel de serviço, aguarde 1 minuto e ligue novamente para ciclar a máquina',
                  ],
                  german: [
                    'Schalten Sie die Hauptversorgung und den Sicherungsautomaten am Servicepanel aus, warten Sie 1 Minute, schalten Sie wieder ein und lassen Sie die Maschine einen Zyklus laufen.',
                  ],
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eult/eult-machine-run.png',
                    },
                  ],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'euht-temperature-issue',
        label: [
          'Temperature issue',
          'Problema de temperatura',
          'Problème de température',
          'Temperatuurprobleem',
          'Problema di temperatura',
          '温度の問題',
          'Problema de temperatura',
          'Temperaturproblem',
        ],
        option: 'troubleshooting.euht.temperature',
        steps: [
          {
            key: 'euht-temperature-items',
            options: [],
            machineIssueListItems: [
              {
                title: {
                  english: 'incoming water temperature is above 120°F',
                  spanish:
                    'La temperatura del agua entrante es superior a 120 °F',
                  french: 'La température de l’eau entrante dépasse 120°F',
                  dutch:
                    'de temperatuur van het binnenkomende water is boven 120°F',
                  italian:
                    'la temperatura dell’acqua in ingresso è superiore a 120°F',
                  japanese: '給水温度が120°F以上である',
                  portuguese:
                    'a temperatura da água de entrada está acima de 120°F',
                  german: 'die einströmende Wassertemperatur liegt über 120°F',
                },
                details: {
                  english: [
                    'Wait for 15 minutes and see if the temperature meets the standard. If not, please use the “I need help” button below to contact Ecolab',
                  ],
                  spanish: [
                    'Espere 15 minutos y compruebe si la temperatura cumple con el estándar. De lo contrario, utilice el botón "Necesito ayuda" a continuación para contactar con Ecolab.',
                  ],
                  french: [
                    'Attendez 15 minutes et vérifiez si la température respecte la norme. Sinon, utilisez le bouton “J’ai besoin d’aide” ci-dessous pour contacter Ecolab.',
                  ],
                  dutch: [
                    'Wacht 15 minuten en controleer of de temperatuur aan de norm voldoet. Zo niet, gebruik dan de knop "Ik heb hulp nodig" hieronder om contact op te nemen met Ecolab',
                  ],
                  italian: [
                    'Attendere 15 minuti e verificare che la temperatura rispetti lo standard. In caso contrario, utilizzare il pulsante “Ho bisogno di aiuto” qui sotto per contattare Ecolab',
                  ],
                  japanese: [
                    '15分待って温度が基準に達しているか確認してください。満たない場合は、下の「助けが必要です」ボタンを使用してエコラボに連絡してください。',
                  ],
                  portuguese: [
                    'Aguarde 15 minutos e verifique se a temperatura atende ao padrão. Caso contrário, use o botão “Preciso de ajuda” abaixo para contactar a Ecolab',
                  ],
                  german: [
                    'Warten Sie 15 Minuten und prüfen Sie, ob die Temperatur dem Standard entspricht. Wenn nicht, verwenden Sie bitte die Schaltfläche „Ich brauche Hilfe“, um Ecolab zu kontaktieren.',
                  ],
                },
                answered: false,
              },
              {
                title: {
                  english: 'hot water from the prescrap sink is above 120°F',
                  spanish:
                    'El agua caliente del fregadero de pre-desguace está a más de 120°F',
                  french: 'L’eau chaude de l’évier de prélavage dépasse 120°F',
                  dutch: 'heet water uit de voorspoelspoelbak is boven 120°F',
                  italian:
                    'l’acqua calda del lavello di prelavaggio è superiore a 120°F',
                  japanese: 'プレスクラップシンクからの湯温が120°Fを超えている',
                  portuguese:
                    'a água quente da pia de pré-lavagem está acima de 120°F',
                  german: 'Heißwasser aus dem Vorspülbecken liegt über 120°F',
                },
                details: {
                  english: [
                    'If the machine is plumbed to cold water, please use the “I need help” button below to contact Ecolab or a plumber for assistance',
                  ],
                  spanish: [
                    'Si la máquina está conectada a agua fría, utilice el botón "Necesito ayuda" a continuación para comunicarse con Ecolab o un plomero para obtener ayuda.',
                  ],
                  french: [
                    'Si la machine est raccordée à l’eau froide, utilisez le bouton “J’ai besoin d’aide” ci-dessous pour contacter Ecolab ou un plombier.',
                  ],
                  dutch: [
                    'Als de machine op koud water is aangesloten, gebruik dan de knop "Ik heb hulp nodig" hieronder om contact op te nemen met Ecolab of een loodgieter voor hulp',
                  ],
                  italian: [
                    'Se la macchina è collegata all’acqua fredda, utilizzare il pulsante “Ho bisogno di aiuto” qui sotto per contattare Ecolab o un idraulico',
                  ],
                  japanese: [
                    '機械が冷水配管に接続されている場合は、下の「助けが必要です」ボタンからエコラボまたは配管業者に連絡してください。',
                  ],
                  portuguese: [
                    'Se a máquina estiver ligada à água fria, use o botão “Preciso de ajuda” abaixo para contactar a Ecolab ou um canalizador/encanador',
                  ],
                  german: [
                    'Wenn die Maschine an Kaltwasser angeschlossen ist, verwenden Sie bitte die Schaltfläche „Ich brauche Hilfe“, um Ecolab oder einen Klempner zu kontaktieren.',
                  ],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'euht-runs-constantly',
        label: [
          'Runs constantly',
          'Corre constantemente',
          'Fonctionne en permanence',
          'Blijft continu draaien',
          'Funziona continuamente',
          '連続運転している',
          'Funciona continuamente',
          'Läuft ständig',
        ],
        option: 'troubleshooting.euht.runs',
        steps: [
          {
            key: 'euht-constant-run-items',
            options: [],
            machineIssueListItems: [
              {
                keyValue: 9,
                title: {
                  english: 'cut power source and turn it back on',
                  spanish: 'Corte la fuente de energía y vuelva a encenderla',
                  french: 'Coupez la source d’alimentation et rallumez-la',
                  dutch: 'haal de stroom eraf en zet vervolgens weer aan',
                  italian: 'togliere alimentazione e riaccendere',
                  japanese: '電源を切り、再度入れてください',
                  portuguese: 'desligue a alimentação e ligue novamente',
                  german: 'Stromquelle trennen und wieder einschalten',
                },
                details: {
                  english: [
                    'Turn off the power and breaker in the service panel, wait 1 minute, then turn it back on and cycle the machine',
                  ],
                  spanish: [
                    'Apague la energía y el disyuntor en el panel de servicio, espere 1 minuto, luego vuelva a encenderlo y cycle la máquina.',
                  ],
                  french: [
                    'Coupez l’alimentation et le disjoncteur dans le panneau de service, attendez 1 minute, puis rallumez et faites tourner la machine.',
                  ],
                  dutch: [
                    'Zet de stroom en zekering/groep in het servicepaneel uit, wacht 1 minuut, zet weer aan en laat de machine een cyclus draaien',
                  ],
                  italian: [
                    "Spegnere l'alimentazione e l'interruttore nel quadro, attendere 1 minuto, quindi riaccendere e far eseguire un ciclo alla macchina",
                  ],
                  japanese: [
                    'サービスパネルの主電源とブレーカーを切り、1分待ってから再投入し、サイクルを実行してください。',
                  ],
                  portuguese: [
                    'Desligue a energia e o disjuntor no painel de serviço, aguarde 1 minuto e ligue novamente para ciclar a máquina',
                  ],
                  german: [
                    'Schalten Sie die Hauptversorgung und den Sicherungsautomaten am Servicepanel aus, warten Sie 1 Minute, schalten Sie wieder ein und lassen Sie die Maschine einen Zyklus laufen.',
                  ],
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eult/eult-machine-run.png',
                    },
                  ],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'euht-door-issue',
        label: [
          'Door issue',
          'Problema con la puerta',
          'Problème de porte',
          'Deurprobleem',
          'Problema con la porta',
          'ドアの問題',
          'Problema na porta',
          'Türproblem',
        ],
        option: 'troubleshooting.euht.door',
        steps: [
          {
            key: 'euht-door-issue-items',
            options: [],
            machineIssueListItems: [
              {
                title: {
                  english: 'no obstructions are preventing the door',
                  spanish:
                    'No hay obstrucciones que impidan el paso de la puerta.',
                  french: "Aucun obstacle n'empêche la porte de s'ouvrir.",
                  dutch: 'er zijn geen obstructies die de deur hinderen',
                  italian: 'nessuna ostruzione impedisce la porta',
                  japanese: 'ドアの開閉を妨げる障害物がない',
                  portuguese: 'não há obstruções impedindo a porta',
                  german: 'keine Hindernisse blockieren die Tür',
                },
                details: {
                  english: [],
                  spanish: [],
                  french: [],
                  dutch: [],
                  italian: [],
                  japanese: [],
                  portuguese: [],
                  german: [],
                  images: [],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'euht-not-draining',
        label: [
          'Not draining',
          'No drenando',
          'Ne pas drainer',
          'Loopt niet leeg',
          'Non scarica',
          '排水しない',
          'Não está drenando',
          'Pumpt nicht ab',
        ],
        option: 'troubleshooting.euht.drain',
        steps: [
          {
            key: 'euht-drain-with-water',
            question: 'troubleshooting.euht.drainWater',
            label: [
              'Is the machine draining all time?',
              '¿La máquina está drenando todo el tiempo?',
              'La machine se décharge-t-elle constamment ?',
              'Loopt de machine voortdurend leeg?',
              'La macchina scarica continuamente?',
              '常に排水し続けていますか？',
              'A máquina está drenando o tempo todo?',
              'Pumpt die Maschine die ganze Zeit ab?',
            ],
            optionsLabel: '',
            description: [
              'Select “No” if the machine is not draining',
              'Seleccione "No" si la máquina no drena',
              'Sélectionnez « Non » si la machine ne se vide pas.',
              'Selecteer "Nee" als de machine niet leegloopt',
              'Selezionare “No” se la macchina non scarica',
              '排水していない場合は「いいえ」を選択してください',
              'Selecione “Não” se a máquina não estiver drenando',
              'Wählen Sie „Nein“, wenn die Maschine nicht abpumpt',
            ],
            images: [
              {
                url: '',
                alt: '',
              },
            ],
            options: [
              {
                key: 'euht-yes',
                option: 'troubleshooting.yes',
                label: ['Yes', 'Si', 'Oui', 'Ja', 'Sì', 'はい', 'Sim', 'Ja'],
                icon: 'check',
                steps: [
                  {
                    key: 'euht-darinlist-items',
                    question: 'Checklist Items',
                    options: [],
                    machineIssueListItems: [
                      {
                        title: {
                          english:
                            'the drain hose/tube has a loop to prevent siphoning (if accessible)',
                          spanish:
                            'la manguera/tubo de drenaje tiene un bucle para evitar el sifón (si es accesible)',
                          french:
                            'le tuyau de vidange a une boucle pour éviter le siphon (si accessible)',
                          dutch:
                            'de afvoerslang/-buis heeft een lus om hevelwerking te voorkomen (indien toegankelijk)',
                          italian:
                            'il tubo/flessibile di scarico ha un’ansa per evitare il sifonaggio (se accessibile)',
                          japanese:
                            '排水ホース／チューブにサイフォン防止用のループがある（アクセス可能な場合）',
                          portuguese:
                            'a mangueira/tubo de dreno tem um laço para evitar sifonagem (se acessível)',
                          german:
                            'Ablaufschlauch/-rohr hat eine Schlaufe zur Vermeidung von Siphoneffekt (falls zugänglich)',
                        },
                        details: {
                          english: [],
                          spanish: [],
                          french: [],
                          dutch: [],
                          italian: [],
                          japanese: [],
                          portuguese: [],
                          german: [],
                          images: [],
                        },
                        answered: false,
                      },
                    ],
                  },
                ],
              },
              {
                key: 'euht-not-sure',
                option: 'troubleshooting.notSure',
                label: [
                  'Not Sure',
                  'No estoy seguro(a)',
                  'Pas sûr',
                  'Niet zeker',
                  'Non sono sicuro(a)',
                  'よくわからない',
                  'Não tenho certeza',
                  'Nicht sicher',
                ],
                icon: 'question',
                resolve: false,
                steps: [],
              },
              {
                key: 'euht-no',
                option: 'troubleshooting.no',
                label: [
                  'No',
                  'No',
                  'Non',
                  'Nee',
                  'No',
                  'いいえ',
                  'Não',
                  'Nein',
                ],
                icon: 'cross',
                steps: [
                  {
                    key: 'euht-drainlist-items-no',
                    question: 'Checklist Items for No',
                    options: [],
                    machineIssueListItems: [
                      {
                        keyValue: 7,
                        title: {
                          english: 'no obstructions are blocking the drain',
                          spanish:
                            'No hay obstrucciones que bloqueen el drenaje.',
                          french: "Aucun objet n'obstrue le drain.",
                          dutch:
                            'er zijn geen obstructies die de afvoer blokkeren',
                          italian: 'nessuna ostruzione blocca lo scarico',
                          japanese: '排水口を塞ぐ異物がない',
                          portuguese: 'não há obstruções bloqueando o dreno',
                          german: 'Keine Hindernisse blockieren den Abfluss',
                        },
                        details: {
                          english: [
                            "If it's completely full, please use the “I need help” button below to contact Ecolab",
                          ],
                          spanish: [
                            'Si está completamente lleno, utilice el botón "Necesito ayuda" a continuación para comunicarse con Ecolab.',
                          ],
                          french: [
                            "S'il est complètement plein, veuillez utiliser le bouton “J’ai besoin d’aide” ci-dessous pour contacter Ecolab",
                          ],
                          dutch: [
                            'Als hij helemaal vol is, gebruik dan de knop "Ik heb hulp nodig" hieronder om contact op te nemen met Ecolab',
                          ],
                          italian: [
                            'Se è completamente pieno, utilizzare il pulsante “Ho bisogno di aiuto” qui sotto per contattare Ecolab',
                          ],
                          japanese: [
                            '完全に満杯の場合は、下の「助けが必要です」ボタンからエコラボに連絡してください。',
                          ],
                          portuguese: [
                            'Se estiver completamente cheio, utilize o botão “Preciso de ajuda” abaixo para contactar a Ecolab',
                          ],
                          german: [
                            'Wenn er komplett voll ist, verwenden Sie bitte die Schaltfläche „Ich brauche Hilfe“, um Ecolab zu kontaktieren.',
                          ],
                          images: [
                            {
                              url: 'assets/images/troubleshooting/eult/eult-drain-1.png',
                            },
                            {
                              url: 'assets/images/troubleshooting/eult/eult-drain-2.png',
                            },
                            {
                              url: 'assets/images/troubleshooting/eult/eult-machine-down-2.png',
                            },
                            {
                              url: 'assets/images/troubleshooting/eult/eult-machine-down-3.png',
                            },
                          ],
                        },
                        answered: false,
                      },
                      {
                        keyValue: 8,
                        title: {
                          english:
                            'drain hoses/tubes not kinked (if accessible)',
                          spanish:
                            'mangueras/tubos de drenaje no retorcidos (si son accesibles)',
                          french:
                            'tuyaux/tubes de drain non pliés (si accessible)',
                          dutch:
                            'afvoerslangen/-buizen niet geknikt (indien toegankelijk)',
                          italian:
                            'tubi/flessibili di scarico non piegati (se accessibili)',
                          japanese:
                            '排水ホース／チューブが折れ曲がっていない（アクセス可能な場合）',
                          portuguese:
                            'mangueiras/tubos de dreno sem dobras (se acessível)',
                          german:
                            'Ablaufschläuche/-rohre nicht geknickt (falls zugänglich)',
                        },
                        details: {
                          english: [],
                          spanish: [],
                          french: [],
                          dutch: [],
                          italian: [],
                          japanese: [],
                          portuguese: [],
                          german: [],
                          images: [
                            {
                              url: 'assets/images/troubleshooting/eult/eult-leaking-3.png',
                            },
                            {
                              url: 'assets/images/troubleshooting/eult/eult-leaking-4.png',
                            },
                          ],
                        },
                        answered: false,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        key: 'euht-making-noise',
        label: [
          'Making noise',
          'Haciendo ruido',
          'Faire du bruit',
          'Maakt lawaai',
          'Fa rumore',
          '異音がする',
          'Faz barulho',
          'Macht Geräusche',
        ],
        option: 'troubleshooting.euht.noise',
        steps: [
          {
            key: 'euht-noise-issue-items',
            options: [],
            machineIssueListItems: [
              {
                keyValue: 13,
                title: {
                  english: 'no obstructions are blocking the drain',
                  spanish: 'No hay obstrucciones que bloqueen el drenaje.',
                  french: "Aucun objet n'obstrue le drain.",
                  dutch: 'er zijn geen obstructies die de afvoer blokkeren',
                  italian: 'nessuna ostruzione blocca lo scarico',
                  japanese: '排水口を塞ぐ異物がない',
                  portuguese: 'não há obstruções bloqueando o dreno',
                  german: 'Keine Hindernisse blockieren den Abfluss',
                },
                details: {
                  english: [
                    "If it's completely full, please use the “I need help” button below to contact Ecolab",
                  ],
                  spanish: [
                    'Si está completamente lleno, utilice el botón "Necesito ayuda" a continuación para comunicarse con Ecolab.',
                  ],
                  french: [
                    "S'il est complètement plein, veuillez utiliser le bouton “J’ai besoin d’aide” ci-dessous pour contacter Ecolab",
                  ],
                  dutch: [
                    'Als hij helemaal vol is, gebruik dan de knop "Ik heb hulp nodig" hieronder om contact op te nemen met Ecolab',
                  ],
                  italian: [
                    'Se è completamente pieno, utilizzare il pulsante “Ho bisogno di aiuto” qui sotto per contattare Ecolab',
                  ],
                  japanese: [
                    '完全に満杯の場合は、下の「助けが必要です」ボタンからエコラボに連絡してください。',
                  ],
                  portuguese: [
                    'Se estiver completamente cheio, utilize o botão “Preciso de ajuda” abaixo para contactar a Ecolab',
                  ],
                  german: [
                    'Wenn er komplett voll ist, verwenden Sie bitte die Schaltfläche „Ich brauche Hilfe“, um Ecolab zu kontaktieren.',
                  ],
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eult/eult-machine-down-2.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/eult/eult-machine-down-3.png',
                    },
                  ],
                },
                answered: false,
              },
              {
                keyValue: 14,
                title: {
                  english: 'there is water in the tank',
                  spanish: 'hay agua en el tanque',
                  french: 'Il y a de l’eau dans le réservoir',
                  dutch: 'er staat water in de tank',
                  italian: 'c’è acqua nel serbatoio',
                  japanese: 'タンク内に水がある',
                  portuguese: 'há água no tanque',
                  german: 'Es befindet sich Wasser im Tank',
                },
                details: {
                  english: [
                    'Close the door. If water does not fill (no sound of water spraying), continue:',
                    '• Refer to the image of the water valve',
                    '• Turn on the valve',
                  ],
                  spanish: [
                    'Cierre la puerta. Si el agua no llena (no hay sonido de agua rociando), continúe:',
                    '• Consulte la imagen de la válvula de agua.',
                    '• Encienda la válvula.',
                  ],
                  french: [
                    "Fermez la porte. Si l'eau ne remplit pas (aucun son de pulvérisation d’eau), continuez :",
                    '• Référez-vous à l’image de la valve d’eau',
                    '• Ouvrez la vanne',
                  ],
                  dutch: [
                    'Sluit de deur. Als er geen water instroomt (geen geluid van sproeiend water), ga dan verder:',
                    '• Raadpleeg de afbeelding van de waterklep',
                    '• Zet de klep open',
                  ],
                  italian: [
                    'Chiudere la porta. Se non entra acqua (nessun rumore di spruzzo), procedere:',
                    '• Fare riferimento all’immagine della valvola dell’acqua',
                    '• Aprire la valvola',
                  ],
                  japanese: [
                    'ドアを閉めてください。給水されない場合（散水音がしない場合）は、次に進んでください：',
                    '・添付の給水バルブ画像を参照',
                    '・バルブを開けてください',
                  ],
                  portuguese: [
                    'Feche a porta. Se a água não entrar (sem som de pulverização), continue:',
                    '• Consulte a imagem da válvula de água',
                    '• Abra a válvula',
                  ],
                  german: [
                    'Schließen Sie die Tür. Wenn kein Wasser einläuft (kein Sprühgeräusch), fahren Sie fort:',
                    '• Siehe Abbildung des Wasserventils',
                    '• Ventil öffnen',
                  ],
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eult/eult-machine-down-1.png',
                    },
                  ],
                },
                answered: false,
              },
              {
                keyValue: 16,
                title: {
                  english: 'water hoses/tubes not kinked (if accessible)',
                  spanish:
                    'mangueras/tubos de agua no retorcidos (si son accesibles)',
                  french: 'tuyaux/tubes d’eau non pliés (si accessible)',
                  dutch:
                    'waterslangen/-buizen niet geknikt (indien toegankelijk)',
                  italian:
                    'tubi/flessibili dell’acqua non piegati (se accessibili)',
                  japanese:
                    '給水ホース／チューブが折れ曲がっていない（アクセス可能な場合）',
                  portuguese:
                    'mangueiras/tubos de água sem dobras (se acessível)',
                  german:
                    'Wasserschläuche/-rohre nicht geknickt (falls zugänglich)',
                },
                details: {
                  english: [],
                  spanish: [],
                  french: [],
                  dutch: [],
                  italian: [],
                  japanese: [],
                  portuguese: [],
                  german: [],
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eult/eult-leaking-3.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/eult/eult-leaking-4.png',
                    },
                  ],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'euht-spray-arm',
        label: [
          'Spray arm',
          'Brazo rociador',
          'Bras de pulvérisation',
          'Sproeiarm',
          'Braccio irroratore',
          'スプレーアーム',
          'Braço pulverizador',
          'Sprüharm',
        ],
        option: 'troubleshooting.euht.sprayArm',
        steps: [
          {
            key: 'euht-spray-arm-items',
            options: [],
            machineIssueListItems: [
              {
                keyValue: 11,
                title: {
                  english: 'nozzles not clogged',
                  spanish: 'boquillas no obstruidas',
                  french: 'Les buses ne sont pas obstruées',
                  dutch: 'sproeiers niet verstopt',
                  italian: 'ugelli non ostruiti',
                  japanese: 'ノズルが詰まっていない',
                  portuguese: 'bicos não obstruídos',
                  german: 'Düsen nicht verstopft',
                },
                details: {
                  english: [],
                  spanish: [],
                  french: [],
                  dutch: [],
                  italian: [],
                  japanese: [],
                  portuguese: [],
                  german: [],
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eult/eult-leaking-1.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/eult/eult-leaking-2.png',
                    },
                  ],
                },
                answered: false,
              },
              {
                keyValue: 12,
                title: {
                  english: 'end caps in place',
                  spanish: 'tapas finales en su lugar',
                  french: 'Les bouchons d’extrémité en place',
                  dutch: 'eindkappen op hun plaats',
                  italian: 'tappi terminali in posizione',
                  japanese: 'エンドキャップが正しく取り付けられている',
                  portuguese: 'tampas de extremidade no lugar',
                  german: 'Endkappen sind an ihrem Platz',
                },
                details: {
                  english: [
                    'May have dropped into the scrap screen or not been replaced after cleaning',
                  ],
                  spanish: [
                    'Es posible que se haya caído al filtro de desechos o que no haya sido reemplazado después de la limpieza.',
                  ],
                  french: [
                    'Peut être tombé dans l’écran à déchets ou n’a pas été remplacé après le nettoyage',
                  ],
                  dutch: [
                    'Kan in het vuilfilter zijn gevallen of niet zijn teruggeplaatst na het schoonmaken',
                  ],
                  italian: [
                    'Potrebbe essere caduto nella griglia dei residui o non essere stato rimesso dopo la pulizia',
                  ],
                  japanese: [
                    '廃棄フィルターに落ちた可能性があるか、清掃後に元に戻されていない可能性があります',
                  ],
                  portuguese: [
                    'Pode ter caído na grelha de resíduos ou não ter sido recolocada após a limpeza',
                  ],
                  german: [
                    'Kann in das Abfallsieb gefallen sein oder wurde nach der Reinigung nicht wieder eingesetzt',
                  ],
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eult/eult-spray-arm-end-cap-1.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/eult/eult-spray-arm-end-cap-2.png',
                    },
                  ],
                },
                answered: false,
              },
              {
                keyValue: 15,
                title: {
                  english: 'arm spins freely',
                  spanish: 'el brazo gira libremente',
                  french: 'Le bras tourne librement',
                  dutch: 'arm draait vrij',
                  italian: 'il braccio gira liberamente',
                  japanese: 'アームが自由に回転する',
                  portuguese: 'o braço gira livremente',
                  german: 'der Arm dreht sich frei',
                },
                details: {
                  english: ['Spin spray arm with your hand '],
                  spanish: ['Gire el brazo rociador con la mano'],
                  french: [
                    'Faites tourner le bras de pulvérisation avec votre main',
                  ],
                  dutch: ['Draai de sproeiarm met de hand'],
                  italian: ['Fai girare il braccio irroratore con la mano'],
                  japanese: ['手でスプレーアームを回してください'],
                  portuguese: ['Gire o braço pulverizador com a mão'],
                  german: ['Drehen Sie den Sprüharm mit der Hand'],
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eult/eult-arm-spin.gif',
                    },
                  ],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'euht-other',
        label: [
          'Other',
          'Otro',
          'Autre',
          'Overig',
          'Altro',
          'その他',
          'Outro',
          'Sonstiges',
        ],
        option: 'troubleshooting.euht.other',
      },
    ],
  };
}
